// StructuralDetails.js — structural "Project Details".
// Everything on the structural form that is not a Report Editor section:
// report type and dates, building description (section 1), inspections (2),
// supporting data (3), deterioration (15–20), overall condition, and the
// engineer and firm. Values default from intake and the Report Editor repairs;
// "Standard" fields follow the repairs until edited.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import SignerPicker from '../components/SignerPicker';
import { rosterFromUser, disciplinePaths } from '../disciplines';
import { getStructuralSections, structuralForm, numberStructuralPhotos } from '../structuralItems';
import { loadStructural } from '../structuralSlots';
import { resolveDetails, repairList, FOLLOWING_KEYS } from '../structuralForm';
import { section1Fields, fieldValue, noticeItem } from '../projectFields';
import '../App.css';

const CONDITIONS = [
  { val: 'passed',      label: '✓ Passed the inspection',                                   color: '#6ee7b7' },
  { val: 'maintenance', label: '⚠ Maintenance needed (not substantial or dangerous)',        color: '#fde68a' },
  { val: 'substantial', label: '🔧 Substantial structural deterioration',                    color: '#fca5a5' },
  { val: 'dangerous',   label: '⚡ Dangerous condition (notify within 10 days)',             color: '#f97316' },
  { val: 'immediate',   label: '🚨 Immediate dangerous condition (notify within 24 hours)',  color: '#ef4444' },
];

const StructuralDetails = () => {
  const { projectId }   = useParams();
  const { currentUser } = useAuth();
  const navigate        = useNavigate();

  const [project, setProject]         = useState(null);
  const [roster, setRoster]           = useState({ engineers: [], firms: [] });
  const [signer, setSigner]           = useState({ engineerId: '', firmId: '' });
  const [stored, setStored]           = useState({});      // engineer's edits only
  const [slots, setSlots]             = useState({});
  const [siteSystems, setSiteSystems] = useState({});
  const [loading, setLoading]         = useState(true);
  const [saveStatus, setSaveStatus]   = useState('');
  const dirty = useRef(false);

  useEffect(() => {
    const load = async () => {
      try {
        const projectDoc = await getDoc(doc(db, 'projects', projectId));
        const proj = projectDoc.exists() ? projectDoc.data() : null;
        setProject(proj);
        const [formDoc, userDoc, report] = await Promise.all([
          getDoc(doc(db, 'projects', projectId, 'forms', 'structural')),
          getDoc(doc(db, 'users', currentUser.uid)),
          loadStructural(projectId, proj?.county),
        ]);
        if (formDoc.exists()) {
          const d = formDoc.data();
          setSigner({ engineerId: d.engineerId || '', firmId: d.firmId || '' });
          setStored(d.details || {});
        }
        setRoster(rosterFromUser(userDoc.exists() ? userDoc.data() : {}));
        setSlots(report.slots);
        setSiteSystems(report.siteSystems);
      } catch (err) {
        console.error('Error loading structural details:', err);
        setSaveStatus('Could not load — refresh to try again');
      }
      setLoading(false);
    };
    load();
  }, [projectId, currentUser]);

  // ─── Derived ────────────────────────────────────────────────────────────

  const county   = project?.county;
  const form     = structuralForm(county);
  const sections = useMemo(() => (project ? getStructuralSections(county) : []), [project, county]);
  const repairs  = useMemo(() => repairList(slots, sections, siteSystems), [slots, sections, siteSystems]);
  const d        = resolveDetails(stored, { project, repairs, condition: stored.condition });
  const photoCount = project ? numberStructuralPhotos(county, slots, siteSystems).filter(c => c.photoId).length : 0;
  const paths    = disciplinePaths(projectId, 'structural');

  // ─── Save (debounced) ───────────────────────────────────────────────────

  useEffect(() => {
    if (loading || !dirty.current) return undefined;
    const t = setTimeout(async () => {
      try {
        await setDoc(doc(db, 'projects', projectId, 'forms', 'structural'),
          { details: stored, engineerId: signer.engineerId, firmId: signer.firmId, updatedAt: serverTimestamp(), updatedBy: currentUser.uid },
          { merge: true });
        dirty.current = false;
        setSaveStatus('Saved ✓');
        setTimeout(() => setSaveStatus(''), 1500);
      } catch (err) {
        console.error('Save error:', err);
        setSaveStatus('Save failed — check your connection');
      }
    }, 800);
    return () => clearTimeout(t);
  }, [stored, signer, loading, projectId, currentUser]);

  const set   = (k, v) => { dirty.current = true; setStored(s => ({ ...s, [k]: v })); };
  const reset = (k) => { dirty.current = true; setStored(s => ({ ...s, [k]: null })); };
  const edited = (k) => stored[k] !== undefined && stored[k] !== null;

  // ─── Field pieces ───────────────────────────────────────────────────────

  const following = (k) => FOLLOWING_KEYS.includes(k);
  const hint = (k) => (following(k) ? (
    edited(k)
      ? <span style={styles.hint}>Edited. <button type="button" style={styles.linkBtn} onClick={() => reset(k)}>Reset to standard</button></span>
      : <span style={styles.hint}>Standard — follows the Report Editor repairs until you edit it.</span>
  ) : null);

  const Text = (k, label, { rows = 0, placeholder = '', width } = {}) => (
    <label key={k} style={{ ...styles.field, ...(width ? { flex: `0 1 ${width}` } : {}) }}>
      <span style={styles.label}>{label}</span>
      {rows
        ? <textarea rows={rows} style={{ ...styles.input, resize: 'vertical' }} value={d[k] ?? ''} placeholder={placeholder} onChange={e => set(k, e.target.value)} />
        : <input style={styles.input} value={d[k] ?? ''} placeholder={placeholder} onChange={e => set(k, e.target.value)} />}
      {hint(k)}
    </label>
  );

  const DateF = (k, label) => (
    <label key={k} style={{ ...styles.field, flex: '0 1 200px' }}>
      <span style={styles.label}>{label}</span>
      <input type="date" style={styles.input} value={d[k] || ''} onChange={e => set(k, e.target.value)} />
    </label>
  );

  const Choice = (k, label, options) => (
    <div key={k} style={{ ...styles.field, flex: '0 1 260px' }}>
      <span style={styles.label}>{label}</span>
      <div role="group" aria-label={label} style={styles.btnRow}>
        {options.map(([v, l]) => (
          <button key={v} type="button" aria-pressed={d[k] === v} style={d[k] === v ? styles.choiceOn : styles.choiceOff}
            onClick={() => set(k, v)}>{l}</button>
        ))}
      </div>
    </div>
  );

  const Check = (k, label) => (
    <label key={k} style={styles.checkRow}>
      <input type="checkbox" checked={!!d[k]} onChange={e => set(k, e.target.checked)} />
      <span>{label}</span>
    </label>
  );

  const ro = (label, value) => (
    <div style={styles.roItem}><span style={styles.roLabel}>{label}:</span> <span>{value || '—'}</span></div>
  );

  if (loading) return <div style={{ marginLeft: '220px', padding: '2rem' }}>Loading...</div>;

  const yesNo = [['Yes', 'Yes'], ['No', 'No']];

  return (
    <div>
      <Sidebar />
      <div style={styles.container}>
        <div style={styles.titleRow}>
          <div>
            <h2 style={styles.title}>Structural — Project Details</h2>
            {project && <p style={styles.subtitle}>{project.address}{project.city ? `, ${project.city}` : ''} — {project.county}</p>}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <span style={styles.saveStatus}>{saveStatus}</span>
            <button type="button" style={styles.editButton} onClick={() => navigate(`/project/${projectId}/edit`)}>Edit Project</button>
          </div>
        </div>

        <div style={styles.banner}>
          <span>Form sections {form === 'broward' ? '2 and 5–13' : '4–14'} are completed in <strong>Report Editor</strong>. {repairs.length} repair item{repairs.length !== 1 ? 's' : ''} there feed the standard text below.</span>
          <button type="button" style={styles.link} onClick={() => navigate(paths.photos)}>Open Report Editor</button>
        </div>

        {/* Engineer and firm */}
        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>Engineer and Firm</h3>
          <SignerPicker roster={roster} engineerId={signer.engineerId} firmId={signer.firmId}
            onChange={(next) => { dirty.current = true; setSigner(next); }} />
        </div>

        {/* Report */}
        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>Report</h3>
          <div style={styles.row}>
            {Choice('reportType', 'Report type', [['initial', 'Initial'], ['amended', 'Amended after repairs']])}
            {form === 'boca' && d.reportType === 'amended' && Text('amendedPermit', 'Repair permit #', { width: '220px' })}
            {ro(`${noticeItem(county)}. Date of notice`, project?.noticeDate)}
            {DateF('inspectStart', 'Inspection commenced')}
            {DateF('inspectEnd', 'Inspection completed')}
          </div>
          {form === 'miami' && (
            <div style={styles.row}>
              {Text('caseNumber', 'Case reference number')}
              {Text('jurisdictionName', 'Jurisdiction name')}
            </div>
          )}
        </div>

        {/* 1. Building */}
        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>1. Description of Building</h3>
          <div style={styles.roGrid}>
            {project && section1Fields(county, ['structural']).map(f => (
              <React.Fragment key={f.key}>{ro(`${f.item}. ${f.label}`, fieldValue(project, f))}</React.Fragment>
            ))}
          </div>
          <p style={styles.hint}>
            These come from the project.{' '}
            <button type="button" style={styles.linkBtn} onClick={() => navigate(`/project/${projectId}/edit`)}>Edit Project</button>
            {' '}to change them. The Report Editor threshold answer, if set, overrides item {form === 'broward' ? 'l' : form === 'boca' ? 'h' : 'n'}.
          </p>
          {Text('additions', 'Additions to the original structure', { rows: 2 })}
          {form === 'broward' && Text('additionalComments', 'Additional comments', { rows: 2 })}
          {form === 'miami' && (
            <>
              <div style={styles.row}>
                {Text('coastDistance', 'Approximate distance to coast and method')}
                {Text('footprintArea', 'Building footprint area (S.F.)', { width: '220px' })}
              </div>
              <div style={styles.btnRow}>
                {Check('legalAttached', 'Legal description attached')}
                {Check('aerialAttached', 'Aerial attached')}
              </div>
            </>
          )}
        </div>

        {/* 2. Inspections */}
        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>{form === 'broward' ? '3. Inspections' : '2. Inspections'}</h3>
          <p style={styles.hint}>Name, license and qualifications print from the engineer selected above{form === 'miami' ? ', with their discipline of practice' : ''}.</p>
          {Text('testing', 'Laboratory or other formal testing', { placeholder: 'N/A' })}
          <div style={styles.subBox}>
            <span style={styles.label}>Structural repairs required: <strong>{repairs.length ? 'Yes' : 'No'}</strong> <em style={{ fontWeight: 400 }}>(from Report Editor repair items)</em></span>
            {Text('repairsDescription', 'Repairs — describe and indicate acceptance', { rows: 3 })}
          </div>
          <div style={styles.row}>
            {Choice('occupancyOk', 'Can the building continue to be occupied during repairs?', yesNo)}
            {form === 'miami' && Choice('vacate', 'Recommend the building be vacated?', yesNo)}
          </div>
          {Text('occupancyExplanation', 'Occupancy — explanation / conditions', { rows: 3 })}
          <div style={styles.row}>
            {Choice('recordResearched', 'Property record researched for violations or unsafe cases?', yesNo)}
            {Text('recordComments', 'Explanation / comments')}
          </div>
        </div>

        {/* 3. Supporting data */}
        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>{form === 'broward' ? '4. Supporting Data' : '3. Supporting Data'}</h3>
          <div style={styles.row}>
            {Text('sheets', 'Additional sheets of written data', { width: '200px' })}
            <div style={{ ...styles.field, flex: '0 1 200px' }}>
              <span style={styles.label}>Photographs</span>
              <span style={styles.autoVal}>{photoCount} <em style={{ fontWeight: 400 }}>(counted from the photo report)</em></span>
            </div>
            {Text('drawings', 'Drawings or sketches', { width: '200px' })}
            {Text('tests', 'Test reports', { width: '200px' })}
          </div>
        </div>

        {/* 15–20 */}
        {form !== 'broward' && (
          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>15. Deterioration{form === 'miami' ? ' (and 16–20)' : ''}</h3>
            {Text('det15a', 'a. Structural deterioration and its extent', { rows: 3 })}
            <p style={styles.hint}>16 and 17 print N/A unless the overall condition is Substantial structural deterioration.</p>
            {Text('det16', form === 'miami' ? '16. Damage, extent and recommendations' : 'b1. Damage, extent and recommendations', { rows: 2 })}
            {Text('det17', form === 'miami' ? '17. Areas requiring added inspection; testing results' : 'b2. Areas requiring added inspection; testing results', { rows: 2 })}
            {Text('det18', form === 'miami' ? '18. Manner and type of inspections performed' : 'b3. Manner and type of inspections performed')}
            {Text('det19', form === 'miami' ? '19. Graded urgency of each recommended repair' : 'b4. Graded urgency of each recommended repair')}
            {Text('det20', form === 'miami' ? '20. Unsafe or dangerous conditions (as defined in the FBC)' : 'b5. Unsafe or dangerous conditions (as defined in the FBC)')}
          </div>
        )}

        {/* Overall condition */}
        {form !== 'broward' ? (
          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>Overall Condition (page 1)</h3>
            <div style={styles.btnRow}>
              {CONDITIONS.map(c => (
                <button key={c.val} type="button" aria-pressed={d.condition === c.val}
                  style={{ ...styles.condBtn, backgroundColor: d.condition === c.val ? c.color : '#f3f4f6', fontWeight: d.condition === c.val ? 600 : 400 }}
                  onClick={() => set('condition', c.val)}>{c.label}</button>
              ))}
            </div>
            {hint('condition')}
            {!d.condition && <p style={{ ...styles.hint, color: '#7f1d1d' }}>Select the condition that applies. There are repair items, so it isn’t set automatically.</p>}
            {form === 'miami' && <div style={{ marginTop: '0.75rem' }}>{Check('unpermitted', 'Unpermitted work identified (Sec. 1804.1 FBC, EB)')}</div>}
          </div>
        ) : (
          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>Page 1</h3>
            <p style={styles.hint}>Broward marks “{repairs.length ? 'Repairs are required as outlined in the attached inspection report' : 'No repairs required'}” from the Report Editor repair items.</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

const styles = {
  container:    { marginLeft: '200px', padding: '2rem', paddingBottom: '5rem' },
  titleRow:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', gap: '1rem', flexWrap: 'wrap' },
  title:        { margin: '0 0 0.25rem', color: '#112b49', fontSize: '1.5rem' },
  subtitle:     { margin: 0, color: '#555', fontSize: '0.9rem' },
  saveStatus:   { fontSize: '0.8rem', color: '#4b5563', fontStyle: 'italic' },
  editButton:   { padding: '0.5rem 0', width: '120px', backgroundColor: '#b45309', color: 'white', border: 'none', borderRadius: '5px', fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  banner:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', backgroundColor: 'rgba(100,149,237,0.1)', border: '1px solid rgba(100,149,237,0.35)', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.875rem', color: '#112b49' },
  link:         { padding: '0.4rem 0.9rem', backgroundColor: '#ffffff', color: '#112b49', border: '1px solid #112b49', borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit' },
  card:         { backgroundColor: 'rgba(255,255,255,0.92)', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  sectionTitle: { margin: 0, fontSize: '1rem', fontWeight: '600', color: '#112b49', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.5rem' },
  row:          { display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start' },
  field:        { display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: '1 1 240px', minWidth: 0 },
  label:        { fontSize: '0.85rem', fontWeight: '500', color: '#374151' },
  input:        { padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.9rem', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  hint:         { margin: 0, fontSize: '0.78rem', color: '#4b5563' },
  linkBtn:      { background: 'none', border: 'none', padding: 0, color: 'cornflowerblue', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.78rem', fontFamily: 'inherit' },
  btnRow:       { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
  choiceOff:    { minHeight: '36px', padding: '0 0.9rem', borderRadius: '6px', border: '1px solid #c3cad5', backgroundColor: '#fff', color: '#374151', fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit' },
  choiceOn:     { minHeight: '36px', padding: '0 0.9rem', borderRadius: '6px', border: '2px solid #112b49', backgroundColor: 'rgba(100,149,237,0.18)', color: '#112b49', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  condBtn:      { padding: '0.6rem 1rem', border: '1px solid #d1d5db', borderRadius: '5px', fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'inherit', color: '#111' },
  checkRow:     { display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: '#374151', cursor: 'pointer' },
  roGrid:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.4rem', fontSize: '0.85rem', color: '#374151' },
  roItem:       { minWidth: 0 },
  roLabel:      { fontWeight: 600, color: '#112b49' },
  subBox:       { display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.75rem', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' },
  autoVal:      { fontSize: '0.95rem', fontWeight: 600, color: '#112b49', padding: '0.45rem 0' },
};

export default StructuralDetails;
